import React, { useEffect, useMemo, useState } from "react";
import { api, asset } from "@/lib/api";
import { IMG } from "@/lib/api";
import { PageHeader } from "@/components/shared/SectionHeader";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const CATS = ["All","Legislation","Current Events","Research","Health","Breeders","Rescue","Conservation","Community News"];

export default function News() {
  const [news, setNews] = useState([]);
  const [cat, setCat] = useState("All");

  useEffect(() => { api.get("/news").then(r => setNews(r.data)); }, []);

  const filtered = useMemo(() => news.filter(n => cat === "All" || n.category === cat), [news, cat]);
  const [breaking] = filtered.filter(n => n.featured);
  const rest = filtered.filter(n => n.id !== breaking?.id);

  return (
    <div>
      <PageHeader
        eyebrow="Wolfdog News"
        title="What's happening across the wolfdog world."
        description="Legislation, research, health, community news and conservation — curated by our editorial team."
        image={IMG.howl}
      />

      <div className="bg-primary text-primary-foreground">
        <div className="container-x py-3 text-xs uppercase tracking-widest font-semibold flex items-center gap-4">
          <span className="rounded-full bg-accent text-accent-foreground px-2 py-0.5">Breaking</span>
          <span>{breaking?.title}</span>
        </div>
      </div>

      <section className="container-x py-14">
        <div className="flex flex-wrap gap-2">
          {CATS.map(c => (
            <button key={c} onClick={() => setCat(c)} data-testid={`news-cat-${c.toLowerCase().replace(/\s+/g,'-')}`}
              className={"text-xs uppercase tracking-[0.2em] font-semibold px-3 py-2 rounded-full border transition " + (cat === c ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-foreground/40")}>
              {c}
            </button>
          ))}
        </div>

        {breaking && (
          <Link to="/news" className="group mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 relative aspect-[16/10] rounded-lg overflow-hidden">
              <img src={asset(breaking.cover)} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <div className="lg:col-span-5">
              <div className="eyebrow">{breaking.category}</div>
              <h2 className="font-display text-4xl md:text-5xl mt-3 leading-[1.02] tracking-tight text-balance">{breaking.title}</h2>
              <p className="mt-4 text-muted-foreground text-pretty">{breaking.excerpt}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold">Read the story <ArrowRight className="h-4 w-4" /></div>
            </div>
          </Link>
        )}

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map(n => (
            <article key={n.id} data-testid={`news-${n.id}`} className="group">
              <div className="relative aspect-[16/10] overflow-hidden rounded-lg border border-border">
                <img src={asset(n.cover)} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="eyebrow mt-4">{n.category} · {new Date(n.date).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
              <h3 className="font-display text-2xl leading-tight mt-2 text-balance">{n.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground text-pretty">{n.excerpt}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
